// Dutch presentation, in one place: € 1.234,56 · 0,1 h · 20 okt 15:11 ·
// "2 min geleden".
//
// Amounts are formatted from their exact decimal strings and never pass
// through a JavaScript number — the API sends "1234.560000", and the comma and
// the thousands dots are inserted into that text. A float round-trip would be
// the one place a cent could change on its way to the screen.
//
// Times are the office's: Europe/Amsterdam, whatever zone the browser is in,
// because "15:11" on a document means the office's afternoon.

const ZONE = 'Europe/Amsterdam';
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** "1234.560000" → "1.234,56"; extra significant decimals are kept, never rounded away. */
export function decimal(amount: string): string {
  const negative = amount.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? amount.slice(1) : amount).split('.');
  const trimmed = fraction.replace(/0+$/, '');
  const shown = trimmed.length <= 2 ? trimmed.padEnd(2, '0') : trimmed;
  const grouped = whole.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '−' : ''}${grouped},${shown}`;
}

/** "€ 1.234,56", "− € 121,00"; a currency other than the euro keeps its code. */
export function money(value: { readonly amount: string; readonly currency: string }): string {
  const negative = value.amount.startsWith('-');
  const digits = decimal(negative ? value.amount.slice(1) : value.amount);
  const unit = value.currency === 'EUR' ? '€' : value.currency;
  return `${negative ? '− ' : ''}${unit} ${digits}`;
}

/** A measured quantity for display: 0.1 → "0,1", with a fixed number of decimals. */
export function number(value: number, decimals = 0): string {
  return value.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** An integer with Dutch grouping: 1234 → "1.234". */
export function count(value: number): string {
  return value.toLocaleString('nl-NL', { maximumFractionDigits: 0 });
}

function parts(instant: Date): { y: number; m: number; d: number; time: string } {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: string) => formatted.find((part) => part.type === type)?.value ?? '';
  return { y: Number(get('year')), m: Number(get('month')), d: Number(get('day')), time: `${get('hour')}:${get('minute')}` };
}

/** "20 okt 15:11"; the year is added when it is not this year. */
export function dateTime(iso: string, now: Date = new Date()): string {
  const at = parts(new Date(iso));
  const year = at.y === parts(now).y ? '' : ` ${at.y}`;
  return `${at.d} ${MONTHS[at.m - 1] ?? ''}${year} ${at.time}`;
}

/** A calendar date "2026-10-20" → "20 okt 2026" (no time zone involved). */
export function date(day: string, withYear = true): string {
  const [y, m, d] = day.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? ''}${withYear ? ` ${y}` : ''}`;
}

/** "zojuist", "2 min geleden", "3 uur geleden", then the date and time. */
export function relative(iso: string, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return 'zojuist';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} uur geleden`;
  return dateTime(iso, now);
}

/** The office's calendar day of an instant: "20 okt 2026". */
export function day(iso: string): string {
  const at = parts(new Date(iso));
  return `${at.d} ${MONTHS[at.m - 1] ?? ''} ${at.y}`;
}
